/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useLoyaltyCardMerge } from "../hooks";
import {
  Box,
  Card,
  Chip,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import infoIcon from "../../../assets/images/Icons/ic_info.svg";
import optInIcon from "../../../assets/images/Icons/ic_tick_filled_blue.svg";
import optOutIcon from "../../../assets/images/Icons/ic_cancel-opt-out.svg";
import {
  ContentLayout,
  HeaderBreadcrumbs,
  LoadingButton,
  useAppConfig,
} from "../../../shared";
import InputControl from "../../../shared/components/InputControl";

/**
 * LoyaltyCardMerge component handles the merging of loyalty card accounts.
 * It allows users to view and select members for merging, displaying their
 * personal and card details.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const LoyaltyCardMerge = () => {
  const theme = useTheme();
  const { taxIdTerm } = useAppConfig();
  // Destructure state and helper functions from custom hook
  const {
    state,
    globalData,
    breadcrumbsLinks,
    fetchMoreMembers,
    findStatusDetails,
    handleCustomerSearch,
    handleMergeLoyaltyCard,
    handleCustomerSelection,
    handleChangeMergePersonalDetails,
  } = useLoyaltyCardMerge();

  // Destructure necessary state variables
  const {
    memberList,
    finalMerge,
    loading,
    mergeButtonLoading,
    selectedMemberDetails,
    mergePersonalDetails,
    targetAccountDetails,
    mergeAccountDetails,
  } = state;

  const VipTableColumns = React.useMemo(() => {
    const address = {
      target: "--",
      merge: "--",
      final: "--",
    };

    const status = {
      target: "--",
      merge: "--",
      final: "--",
    };

    const points = {
      target: "--",
      merge: "--",
      final: "--",
    };

    // Retrieve and display points
    if (targetAccountDetails.business.business_address) {
      const {
        state,
        suburb,
        postcode,
        street_name,
        unit_number,
        street_number,
      } = targetAccountDetails.business.business_address;

      const order = [
        unit_number,
        street_number,
        street_name,
        suburb,
        postcode,
        state,
      ];

      address.target = order.filter(Boolean).join(", ");
    }

    if (mergeAccountDetails.business.business_address) {
      const {
        state,
        suburb,
        postcode,
        street_name,
        unit_number,
        street_number,
      } = mergeAccountDetails.business.business_address;

      const order = [
        unit_number,
        street_number,
        street_name,
        suburb,
        postcode,
        state,
      ];

      address.merge = order.filter(Boolean).join(", ");
    }

    if (finalMerge.business.business_address) {
      const {
        state,
        suburb,
        postcode,
        street_name,
        unit_number,
        street_number,
      } = finalMerge.business.business_address;

      const order = [
        unit_number,
        street_number,
        street_name,
        suburb,
        postcode,
        state,
      ];

      address.final = order.filter(Boolean).join(", ");
    }

    // Retrieve and display status label
    if (targetAccountDetails.card.status) {
      const statusDetails = findStatusDetails(
        targetAccountDetails.card.status,
        globalData
      );
      status.target = statusDetails ? (
        <Chip
          label={statusDetails.label}
          variant="filled"
          size="small"
          sx={{ backgroundColor: "#E1FBF2", color: "#0F875B" }}
        />
      ) : (
        "--"
      );
    }

    if (mergeAccountDetails.card.status) {
      const statusDetails = findStatusDetails(
        mergeAccountDetails.card.status,
        globalData
      );
      status.merge = statusDetails ? (
        <Chip
          label={statusDetails.label}
          variant="filled"
          size="small"
          sx={{ backgroundColor: "#E1FBF2", color: "#0F875B" }}
        />
      ) : (
        "--"
      );
    }

    if (finalMerge.card.status) {
      const statusDetails = findStatusDetails(
        finalMerge.card.status,
        globalData
      );
      status.final = statusDetails ? (
        <Chip
          label={statusDetails.label}
          variant="filled"
          size="small"
          sx={{ backgroundColor: "#E1FBF2", color: "#0F875B" }}
        />
      ) : (
        "--"
      );
    }

    if (targetAccountDetails.card.points) {
      points.target = (
        <Typography color="#C07302" fontWeight={600}>
          {targetAccountDetails.card.points_in_dollars}
        </Typography>
      );
    }

    if (mergeAccountDetails.card.points) {
      points.merge = (
        <Typography color="#C07302" fontWeight={600}>
          {mergeAccountDetails.card.points_in_dollars}
        </Typography>
      );
    }

    if (finalMerge.card.points) {
      points.final = (
        <Typography color="#C07302" fontWeight={600}>
          {finalMerge.card.points_in_dollars}
        </Typography>
      );
    }

    return [
      {
        section: "Personal Details",
        columns: [
          {
            target: {
              label: "Name",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                targetAccountDetails.personal.customer_name
              ),
            },
            merge: {
              label: "Name",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                mergeAccountDetails.personal.customer_name ?? "--"
              ),
            },
            final: {
              label: "Name",
              value: loading.finalMerge ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                finalMerge.personal.customer_name ?? "--"
              ),
            },
          },
          {
            target: {
              label: "Customer Verification",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : targetAccountDetails.personal.customer_verified ? (
                <Stack alignItems="center" flexDirection="row" gap="4px">
                  <img
                    style={{ height: "14px" }}
                    alt="verify"
                    src="/icons/ic_verified_badge.svg"
                  />
                  <Typography fontSize={14}>Verified</Typography>
                </Stack>
              ) : (
                "--"
              ),
            },
            merge: {
              label: "Customer Verification",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : mergeAccountDetails.personal.customer_verified ? (
                <Stack alignItems="center" flexDirection="row" gap="4px">
                  <img
                    style={{ height: "14px" }}
                    alt="verify"
                    src="/icons/ic_verified_badge.svg"
                  />
                  <Typography fontSize={14}>Verified</Typography>
                </Stack>
              ) : (
                "--"
              ),
            },
            final: {
              label: "Customer Verification",
              value: loading.finalMerge ? (
                <Skeleton width="100px" height="14px" />
              ) : finalMerge.personal.customer_verified ? (
                <Stack alignItems="center" flexDirection="row" gap="4px">
                  <img
                    style={{ height: "14px" }}
                    alt="verify"
                    src="/icons/ic_verified_badge.svg"
                  />
                  <Typography fontSize={14}>Verified</Typography>
                </Stack>
              ) : (
                "--"
              ),
            },
          },
          {
            target: {
              label: "Email",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                targetAccountDetails.personal.customer_email
              ),
            },
            merge: {
              label: "Email",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                mergeAccountDetails.personal.customer_email ?? "--"
              ),
            },
            final: {
              label: "Email",
              value: loading.finalMerge ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                finalMerge.personal.customer_email ?? "--"
              ),
            },
          },
          {
            target: {
              label: "Phone Number",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                targetAccountDetails.personal.customer_phone
              ),
            },
            merge: {
              label: "Phone Number",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                mergeAccountDetails.personal.customer_phone ?? "--"
              ),
            },
            final: {
              label: "Phone Number",
              value: loading.finalMerge ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                finalMerge.personal.customer_phone ?? "--"
              ),
            },
          },
          {
            target: {
              label: "Date of Birth",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                targetAccountDetails.personal.date_of_birth
              ),
            },
            merge: {
              label: "Date of Birth",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                mergeAccountDetails.personal.date_of_birth ?? "--"
              ),
            },
            final: {
              label: "Date of Birth",
              value: loading.finalMerge ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                finalMerge.personal.date_of_birth ?? "--"
              ),
            },
          },
          {
            target: {
              label: "Gender",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                targetAccountDetails.personal.gender
              ),
            },
            merge: {
              label: "Gender",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                mergeAccountDetails.personal.gender ?? "--"
              ),
            },
            final: {
              label: "Gender",
              value: loading.finalMerge ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                finalMerge.personal.gender ?? "--"
              ),
            },
          },
          {
            target: {
              label: "Opt In",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : targetAccountDetails.personal.opt_in === null ? (
                "--"
              ) : (
                <Box
                  component="img"
                  src={
                    targetAccountDetails.personal.opt_in
                      ? optInIcon
                      : optOutIcon
                  }
                  width="16px"
                  height="16px"
                />
              ),
            },
            merge: {
              label: "Opt In",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : mergeAccountDetails.personal.opt_in === null ? (
                "--"
              ) : (
                <Box
                  component="img"
                  src={
                    mergeAccountDetails.personal.opt_in ? optInIcon : optOutIcon
                  }
                  width="16px"
                  height="16px"
                />
              ),
            },
            final: {
              label: "Opt In",
              value: loading.finalMerge ? (
                <Skeleton width="100px" height="14px" />
              ) : finalMerge.personal.opt_in === null ? (
                "--"
              ) : (
                <Box
                  component="img"
                  src={finalMerge.personal.opt_in ? optInIcon : optOutIcon}
                  width="16px"
                  height="16px"
                />
              ),
            },
          },
        ],
      },
      {
        section: "Card Details",
        columns: [
          {
            target: {
              label: "Barcode",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                targetAccountDetails.card.barcode
              ),
            },
            merge: {
              label: "Barcode",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                mergeAccountDetails.card.barcode ?? "--"
              ),
            },
            final: {
              label: "Barcode",
              value: loading.finalMerge ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                finalMerge.card.barcode ?? "--"
              ),
            },
          },
          {
            target: {
              label: "Member Number",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                targetAccountDetails.card.member_number
              ),
            },
            merge: {
              label: "Member Number",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                mergeAccountDetails.card.member_number ?? "--"
              ),
            },
            final: {
              label: "Member Number",
              value: loading.finalMerge ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                finalMerge.card.member_number ?? "--"
              ),
            },
          },
          {
            target: {
              label: "Verification Code",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                targetAccountDetails.card.verification_code
              ),
            },
            merge: {
              label: "Verification Code",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                mergeAccountDetails.card.verification_code ?? "--"
              ),
            },
            final: {
              label: "Verification Code",
              value: loading.finalMerge ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                finalMerge.card.verification_code ?? "--"
              ),
            },
          },
          {
            target: {
              label: "Status",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                status.target
              ),
            },
            merge: {
              label: "Status",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                status.merge
              ),
            },
            final: {
              label: "Status",
              value: loading.finalMerge ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                status.final
              ),
            },
          },
          {
            target: {
              label: "Points",
              value: loading.targetAccountDetails ? (
                <Skeleton width="100px" height="14px" />
              ) : (
                points.target
              ),
            },
            merge: {
              label: "Points",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                points.merge
              ),
            },
            final: {
              label: "Points",
              value: loading.finalMerge ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                points.final
              ),
            },
          },
        ],
      },
      {
        section: "Business Details",
        columns: [
          {
            target: {
              label: "Business Name",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                targetAccountDetails.business.business_name ?? "--"
              ),
            },
            merge: {
              label: "Business Name",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                mergeAccountDetails.business.business_name ?? "--"
              ),
            },
            final: {
              label: "Business Name",
              value: loading.finalMerge ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                finalMerge.business.business_name ?? "--"
              ),
            },
          },
          {
            target: {
              label: taxIdTerm,
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                targetAccountDetails.business.abn_number ?? "--"
              ),
            },
            merge: {
              label: taxIdTerm,
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                mergeAccountDetails.business.abn_number ?? "--"
              ),
            },
            final: {
              label: taxIdTerm,
              value: loading.finalMerge ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                finalMerge.business.abn_number ?? "--"
              ),
            },
          },
          {
            target: {
              label: "Business Address",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                address.target ?? "--"
              ),
            },
            merge: {
              label: "Business Address",
              value: loading.mergeAccountDetails ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                address.merge ?? "--"
              ),
            },
            final: {
              label: "Business Address",
              value: loading.finalMerge ? (
                <Skeleton width="100%" height="14px" />
              ) : (
                address.final ?? "--"
              ),
            },
          },
        ],
      },
    ];
  }, [state, taxIdTerm]);

  return (
    <ContentLayout
      title="Merge"
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbsLinks} />}
      rightContent={
        <LoadingButton
          variant="contained"
          loading={mergeButtonLoading}
          onClick={handleMergeLoyaltyCard}
          disabled={!finalMerge.personal.customer_name}
        >
          Merge
        </LoadingButton>
      }
    >
      <Card
        sx={{
          overflow: "auto",
          ".MuiCard-root": {
            margin: 0,
          },
        }}
      >
        <Stack
          py="32px"
          px="24px"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            fontSize="14px"
            fontWeight="500"
            lineHeight="16.94px"
            color="text.secondary"
          >
            Note: Upon merging, points and any linked sponsorship cards will be
            transferred to the target account.
          </Typography>
          <InputControl
            type="switch"
            label="Merge Personal  & Business Details"
            labelPlacement="start"
            disabled={!selectedMemberDetails?.customer_id || !targetAccountDetails.personal?.customer_id}
            checked={mergePersonalDetails}
            onChange={handleChangeMergePersonalDetails}
          />
        </Stack>

        <Stack>
          <Table>
            <TableHead sx={{ background: theme.palette.gray[90] }}>
              <TableRow>
                {[
                  "Target Account (Customer keeps)",
                  <Stack gap="8px">
                    <InputControl
                      type="dropdown"
                      name="memberNumber"
                      label="Member Number"
                      options={memberList.options}
                      value={selectedMemberDetails}
                      loading={memberList.loading}
                      onChange={handleCustomerSelection}
                      getOptionLabel={(option) => option.member_number}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderOption={(getListitemProps, option) => {
                        return (
                          <React.Fragment key={option.id}>
                            <ListItem {...getListitemProps}>
                              <ListItemText
                                primary={option.member_number}
                                secondary={option.customer_name}
                              />
                            </ListItem>

                            {/* Showing load more option */}
                            {getListitemProps["data-option-index"] ===
                              memberList.options.length - 1 &&
                              memberList.pagination.hasMorePages && (
                                <Stack
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  Loading...
                                </Stack>
                              )}
                          </React.Fragment>
                        );
                      }}
                      onInputChange={handleCustomerSearch}
                      inputLabel="Member Number"
                      placeholder="00000"
                      sx={{
                        background: "#ffffff",
                        borderRadius: 1,
                        outline: "#808080",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#808080",
                          },
                        },
                      }}
                      renderInput={(getTextfieldProps) => (
                        <TextField
                          {...getTextfieldProps}
                          label="Member Number"
                          placeholder="Member Number"
                        />
                      )}
                      ListboxProps={{
                        onScroll: (event) => {
                          const listboxNode = event.currentTarget;
                          const threshold = 5; // Adjust as needed
                          const { pagination } = memberList;

                          // Check if we need to load more members
                          if (
                            listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold
                          ) {
                            const currentPage = pagination.pageIndex + 1;
                            if (
                              pagination.hasMorePages &&
                              currentPage <= pagination.lastPage
                            ) {
                              fetchMoreMembers(pagination.pageIndex + 1);
                            }
                          }
                        },
                      }}
                    />
                    <Stack flexDirection="row" alignItems="center" gap="4px">
                      <Box
                        component="img"
                        src={infoIcon}
                        alt="info icon"
                        width="12px"
                        height="12px"
                      />
                      <Typography
                        alignItems="center"
                        display="flex"
                        gap="8px"
                        fontSize="12px"
                        color="#636363"
                      >
                        Account to Merge (Will be deleted)
                      </Typography>
                    </Stack>
                  </Stack>,
                  "Final Merge (Preview)",
                ].map((head, index) => (
                  <TableCell colSpan={2} key={"sectionHead-" + index}>
                    <Typography fontWeight={600} fontSize="16px">
                      {head}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {VipTableColumns.map((sections, index) => {
                return (
                  <React.Fragment key={"column-" + index}>
                    <TableRow>
                      {Array.from({ length: 3 }, (_, index) => {
                        return (
                          <TableCell
                            key={"section-" + index}
                            colSpan={2}
                            sx={{
                              backgroundColor: theme.palette.gray[97],
                              borderBottom:
                                "1px solid rgba(212, 212, 212, 0.5)",
                              borderRight: "1px solid rgba(212, 212, 212, 0.5)",
                              fontWeight: 500,
                              fontSize: 15,
                            }}
                          >
                            {sections.section}
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {sections.columns.map((column, index) => {
                      return (
                        <TableRow key={"sub-column-" + index}>
                          {Object.keys(column).map((key, index) => {
                            return (
                              <React.Fragment key={index}>
                                <TableCell
                                  sx={{
                                    borderBottom:
                                      "1px solid rgba(212, 212, 212, 0.5)",
                                    width: "178px",
                                    fontWeight: 500,
                                  }}
                                >
                                  {column[key].label}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    borderBottom:
                                      "1px solid rgba(212, 212, 212, 0.5)",
                                    borderRight:
                                      "1px solid rgba(212, 212, 212, 0.5)",
                                    width: "270px",
                                  }}
                                >
                                  {column[key].value}
                                </TableCell>
                              </React.Fragment>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Stack>
      </Card>
    </ContentLayout>
  );
};
